import { IMarketCap } from '@/services/apis/fund/schemas';
import {
  TInteractiveBarChartData,
  TInteractiveBarChartPeriod,
} from '@/components/InteractiveBarChart';
import { toFloorFixed } from '@/utils/number';
import { convertArrayToObject } from '@/utils/object';
import type { IFundDetails } from '@/modules/invest/subModules/fund/types';

const fundPeriodsKeys: Record<TInteractiveBarChartPeriod, string> = {
  '1W': 'return1W',
  '1M': 'return1M',
  '3M': 'return3M',
  '6M': 'return6M',
  '1Y': 'return1Y',
  '3Y': 'return3Y',
  '5Y': 'return5Y',
};

const marketCapPeriodsKeys: Record<TInteractiveBarChartPeriod, string> = {
  '1W': 'oneWeekAgo',
  '1M': 'oneMonthAgo',
  '3M': 'threeMonthsAgo',
  '6M': 'sixMonthsAgo',
  '1Y': 'oneYearAgo',
  '3Y': 'threeYearsAgo',
  '5Y': 'fiveYearsAgo',
};

export const transferFundDetailsToData = (
  fundDetails: IFundDetails,
  data: TInteractiveBarChartData
): TInteractiveBarChartData => {
  if (fundDetails) {
    const dataKeys = Object.keys(data) as TInteractiveBarChartPeriod[];

    dataKeys.forEach((dataKey) => {
      const dataValue = data[dataKey];

      if (dataValue) {
        const key = fundPeriodsKeys[dataKey];

        const value = fundDetails[key as never];
        if (value === undefined) {
          delete data[dataKey];
        } else {
          const isDisabled = dataValue.isDisabled || value === 0;

          const item = {
            name: fundDetails.title,
            value: Number(toFloorFixed(value)),
          };

          dataValue.isDisabled = isDisabled;
          dataValue.items.push(item);
        }
      }
    });
  }

  return data;
};

export const transferMarketCapToData = (
  marketCap: IMarketCap[],
  data: TInteractiveBarChartData
) => {
  const marketCapObject = convertArrayToObject(marketCap, 'from');

  const dataKeys = Object.keys(data) as TInteractiveBarChartPeriod[];

  dataKeys.forEach((dataKey) => {
    const dataValue = data[dataKey];

    if (dataValue) {
      const key = marketCapPeriodsKeys[dataKey] as never;

      const marketCapItems = (marketCapObject[key] as IMarketCap)?.symbols;
      if (marketCapItems === undefined) {
        delete data[dataKey];
      } else {
        marketCapItems.forEach((marketCapItem) => {
          const efficiency = marketCapItem.growthRate;
          const isDisabled = dataValue.isDisabled || efficiency === 0;

          const item = {
            name: marketCapItem.titleFa,
            value: Number(toFloorFixed(efficiency)),
            id: marketCapItem.symbolName,
          };

          dataValue.isDisabled = isDisabled;
          dataValue.items.push(item);
        });
      }
    }
  });

  return data;
};
